import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BookOpenCheck,
  ClipboardCheck,
  BookCopy,
  Boxes,
  Building2,
  Crosshair,
  DollarSign,
  GaugeCircle,
  GraduationCap,
  Hammer,
  LayoutDashboard,
  Library,
  ShieldCheck,
  Target,
  TerminalSquare,
  Users,
} from 'lucide-react';

/**
 * Navigation configuration — single source of truth for the AppShell side-bar.
 *
 * Each entry maps a route path to its label + icon + section. Sections cluster
 * the nav into logical groups (per `docs/PLAN.md` Information Architecture).
 *
 * `roles` (optional) restricts the entry to users with one of the listed
 * roles. Routes without a `roles` field are visible to all authenticated users.
 */

import type { UserRole } from '../../store/useAuthStore';

export type NavSection = 'Training' | 'Instructor' | 'Operate' | 'Author' | 'Admin';

export interface NavEntry {
  path: string;
  label: string;
  icon: LucideIcon;
  section: NavSection;
  roles?: ReadonlyArray<UserRole>;
}

export const NAV_ENTRIES: ReadonlyArray<NavEntry> = [
  // Trainees land on /trainee. Other roles land on / (instructor dashboard).
  {
    path: '/trainee',
    label: 'My Dashboard',
    icon: Target,
    section: 'Training',
    roles: ['trainee'],
  },
  {
    path: '/',
    label: 'Dashboard',
    icon: LayoutDashboard,
    section: 'Training',
    roles: ['instructor', 'org_admin', 'superadmin'],
  },
  // Scenario library = instructor view. Trainees see only their assigned
  // mission via /trainee — no peeking at detection rule answer keys.
  {
    path: '/scenarios',
    label: 'Scenarios',
    icon: Crosshair,
    section: 'Training',
    roles: ['instructor', 'org_admin', 'superadmin'],
  },
  // Everyone gets these tabs, but the pages scope themselves: a trainee sees
  // only the courses they're enrolled in and the labs assigned to their
  // cohorts (empty until an instructor assigns something), never the full
  // catalog; instructors/admins see the whole catalog.
  { path: '/courses', label: 'Courses', icon: Library, section: 'Training' },
  { path: '/labs', label: 'Labs', icon: TerminalSquare, section: 'Training' },
  // Instructor layer (2026-09-18): the cohort × lab matrix and the queue of
  // answers waiting on a human. Instructors see the cohorts they own.
  {
    path: '/admin/gradebook',
    label: 'Gradebook',
    icon: BookOpenCheck,
    section: 'Training',
    roles: ['instructor', 'org_admin', 'superadmin'],
  },
  {
    path: '/admin/review',
    label: 'Review queue',
    icon: ClipboardCheck,
    section: 'Training',
    roles: ['instructor', 'org_admin', 'superadmin'],
  },
  // Ranges list = instructor's deploy + monitor view. Trainees see only
  // their assigned range via /trainee.
  {
    path: '/ranges',
    label: 'Ranges',
    icon: Boxes,
    section: 'Operate',
    // Visible to trainees too — the list is backend-scoped to the ranges
    // deployed for them, and the page hides deploy/destroy controls for
    // non-staff (see ranges.index).
  },
  {
    path: '/range-health',
    label: 'Range Health',
    icon: GaugeCircle,
    section: 'Operate',
    roles: ['org_admin', 'superadmin'],
  },
  {
    path: '/monitoring',
    label: 'Monitoring',
    icon: Activity,
    section: 'Operate',
    roles: ['org_admin', 'superadmin'],
  },
  {
    path: '/cost',
    label: 'Cost',
    icon: DollarSign,
    section: 'Operate',
    roles: ['org_admin', 'superadmin'],
  },
  {
    path: '/blueprints',
    label: 'Blueprints',
    icon: BookCopy,
    section: 'Author',
    roles: ['org_admin', 'superadmin'],
  },
  {
    path: '/builder',
    label: 'Scenario Builder',
    icon: Hammer,
    section: 'Author',
    roles: ['org_admin', 'superadmin'],
  },
  // Instructor-only workspace at /instructor/* (its own directory, org-scoped).
  // org_admin/superadmin do NOT get these — they use the Admin hub (/admin),
  // which already links to Users/Cohorts/Organizations; a duplicate "Manage"
  // section was repetitive for them.
  { path: '/instructor', label: 'Overview', icon: GraduationCap, section: 'Instructor', roles: ['instructor'] },
  { path: '/instructor/users', label: 'Users', icon: Users, section: 'Instructor', roles: ['instructor'] },
  { path: '/instructor/cohorts', label: 'Cohorts', icon: Boxes, section: 'Instructor', roles: ['instructor'] },
  { path: '/instructor/organization', label: 'Organization', icon: Building2, section: 'Instructor', roles: ['instructor'] },
  {
    path: '/admin',
    label: 'Admin',
    icon: ShieldCheck,
    section: 'Admin',
    roles: ['org_admin', 'superadmin'],
  },
];

export const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  'Training',
  'Instructor',
  'Operate',
  'Author',
  'Admin',
];
